// src/components/tokenPage/valuation/FeesDynamicsCard.tsx

import React, { useMemo, useState } from "react";
import TimeRangePicker from "@/components/common/TimeRangePicker";
import { DataCard } from "@/components/ui";
import { TimeSeriesChart } from "@/components/charts/base";
import { formatCurrencyShort } from "@/utils/charts/formatters";
import { formatDateLabel } from "@/utils/charts/dataUtils";
import { useTokenFundamentals } from "@/contexts/token";
import { sliceByRange } from "@/lib/metrics";

type Range = '90d' | '180d' | '365d' | 'All';

/**
 * FeesDynamicsCard - Shows historical fees time series
 * 
 * Uses TokenFundamentalsContext for data (no inline fetching).
 */
const FeesDynamicsCard: React.FC = () => {
  const [range, setRange] = useState<Range>('365d');
  const { fees, isLoading } = useTokenFundamentals();

  const { dates, values } = useMemo(() => {
    if (fees.dates.length === 0) {
      return { dates: [], values: [] };
    }
    
    const targetLen = range === '90d' ? 90 : range === '180d' ? 180 : range === '365d' ? 365 : fees.dates.length;
    return sliceByRange(fees.dates, fees.values, targetLen);
  }, [fees, range]);

  // Hide if no data available (after loading)
  if (!isLoading && (!values.length || values.every(v => !Number.isFinite(v)))) {
    return null;
  }

  return (
    <DataCard
      title="Fees"
      subtitle="Activity proxy over time"
      icon="banknote"
      iconColor="text-emerald-400"
      isLoading={isLoading}
      loadingVariant="chart"
      actions={
        <TimeRangePicker options={['90d','180d','365d','All'] as const} value={range} onChange={setRange} theme="blue" />
      }
    >
      <TimeSeriesChart
        dates={dates}
        series={[{ name: 'Fees', values, color: '#34D399', areaStyle: true }]}
        height={260}
        yAxisFormat="currency"
        tooltipFormatter={(params) => {
          const items = params as Array<{ axisValue: string; value: number }>;
          if (!items || items.length === 0) return '';
          const item = items[0];
          const dateStr = item?.axisValue || '';
          const formattedDate = formatDateLabel(dateStr, 'medium');
          const val = Number(item?.value ?? 0);
          const formattedValue = Number.isFinite(val) ? formatCurrencyShort(val) : 'N/A';
          return `<div style="font-size:11px;font-weight:500;margin-bottom:4px;color:#9ca3af;">${formattedDate}</div><div style="font-weight:600;color:#f3f4f6;">${formattedValue}</div>`;
        }}
        showLegend={false}
      />
    </DataCard>
  );
};

export default FeesDynamicsCard;
